import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { teacherAPI } from '../services/api';
import { toast } from 'react-toastify';
import { FiArrowLeft, FiUpload } from 'react-icons/fi';

const AddTeacher = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  
  // Dropdown data
  const [roles, setRoles] = useState([]);
  const [designations, setDesignations] = useState([]);
  const [departments, setDepartments] = useState([]);
  
  // Form data - exactly like admin form
  const [formData, setFormData] = useState({
    role: '',
    designation: '',
    department: '',
    name: '',
    surname: '',
    father_name: '',
    mother_name: '',
    email: '',
    gender: '',
    dob: '',
    date_of_joining: '',
    contactno: '',
    emergency_no: '',
    marital_status: '',
    address: '',
    permanent_address: '',
    qualification: '',
    work_exp: '',
    note: '',
    commision: '',
    discount: '',
    epf_no: '',
    basic_salary: '',
    contract_type: '',
    shift: '',
    location: '',
    festival_leave: '',
    emergency_leave: '',
    regular_leave: '',
    account_title: '',
    bank_account_no: '',
    bank_name: '',
    ifsc_code: '',
    bank_branch: '',
    facebook: '',
    twitter: '',
    linkedin: '',
    instagram: '',
    photo: null,
    resume: null,
    other_doc: null,
    joining_letter: null,
    resignation_letter: null
  });
  
  const [photoPreview, setPhotoPreview] = useState(null);

  useEffect(() => {
    fetchFormData();
    if (id) {
      fetchTeacherData(id);
    }
  }, [id]);

  const fetchFormData = async () => {
    try {
      const response = await teacherAPI.getFormData();
      if (response.data.success) {
        setRoles(response.data.data.roles);
        setDesignations(response.data.data.designations);
        setDepartments(response.data.data.departments);
      }
    } catch (error) {
      toast.error('Error loading form data');
      console.error(error);
    }finally {
      setLoading(false);  // Add this line
    }
  };

  const fetchTeacherData = async (teacherId) => {
    setLoading(true);
    try {
      const response = await teacherAPI.getTeacher(teacherId);
      if (response.data.success) {
        const teacher = response.data.data;
        setFormData({
          role: teacher.role || '',
          designation: teacher.designation || '',
          department: teacher.department || '',
          name: teacher.name || '',
          surname: teacher.surname || '',
          father_name: teacher.father_name || '',
          mother_name: teacher.mother_name || '',
          email: teacher.email || '',
          gender: teacher.gender || '',
          dob: teacher.dob || '',
          date_of_joining: teacher.date_of_joining || '',
          contactno: teacher.contact_no || '',
          emergency_no: teacher.emergency_contact_no || '',
          marital_status: teacher.marital_status || '',
          address: teacher.local_address || '',
          permanent_address: teacher.permanent_address || '',
          qualification: teacher.qualification || '',
          work_exp: teacher.work_exp || '',
          note: teacher.note || '',
          commision: teacher.commision || '',
          discount: teacher.discount || '',
          epf_no: teacher.epf_no || '',
          basic_salary: teacher.basic_salary || '',
          contract_type: teacher.contract_type || '',
          shift: teacher.shift || '',
          location: teacher.location || '',
          festival_leave: teacher.festival_leave || '',
          emergency_leave: teacher.emergency_leave || '',
          regular_leave: teacher.regular_leave || '',
          account_title: teacher.account_title || '',
          bank_account_no: teacher.bank_account_no || '',
          bank_name: teacher.bank_name || '',
          ifsc_code: teacher.ifsc_code || '',
          bank_branch: teacher.bank_branch || '',
          facebook: teacher.facebook || '',
          twitter: teacher.twitter || '',
          linkedin: teacher.linkedin || '',
          instagram: teacher.instagram || '',
          photo: null,
          resume: null,
          other_doc: null,
          joining_letter: null,
          resignation_letter: null
        });
        
        // Set photo preview if exists
        if (teacher.image) {
          setPhotoPreview(teacher.image);
        }
      }
    } catch (error) {
      toast.error('Error loading teacher data');
      console.error(error);
      navigate('/teachers');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleFileChange = (e) => {
    const { name, files } = e.target;
    if (files[0]) {
      setFormData({ ...formData, [name]: files[0] });
      
      if (name === 'photo') {
        setPhotoPreview(URL.createObjectURL(files[0]));
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const data = new FormData();
      
      // Append all form fields - exactly like admin
      Object.keys(formData).forEach((key) => {
        if (formData[key] !== null && formData[key] !== '') {
          data.append(key, formData[key]);
        }
      });

      // Use update API if editing, add API if creating new
      const response = id 
        ? await teacherAPI.updateTeacher(id, data)
        : await teacherAPI.addTeacher(data);
      
      if (response.data.success) {
        toast.success(response.data.message);
        navigate('/teachers');
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
      if (error.response?.data?.errors) {
        Object.values(error.response.data.errors).forEach((err) => {
          toast.error(err[0]);
        });
      } else {
        toast.error(error.response?.data?.message || `Error ${id ? 'updating' : 'adding'} teacher`);
      }
      console.error(error);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-16">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <button
            onClick={() => navigate('/teachers')}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-2"
          >
            <FiArrowLeft /> Back to Teachers
          </button>
          <h1 className="text-3xl font-bold text-gray-900">{id ? 'Edit Staff' : 'Add Staff'}</h1>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow-md p-6">
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
          <p className="text-sm text-blue-800">
            Staff email is their login username, password is generated automatically and sent to staff email.
          </p>
        </div>

        {/* Basic Information */}
        <div className="mb-8">
          <h2 className="text-xl font-bold text-gray-900 mb-4 pb-2 border-b">Basic Information</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Role <span className="text-red-500">*</span>
              </label>
              <select
                name="role"
                value={formData.role}
                onChange={handleInputChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-600 outline-none"
                required
              >
                <option value="">Select</option>
                {roles.map((role) => (
                  <option key={role.id} value={role.id}>{role.name}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Designation</label>
              <select
                name="designation"
                value={formData.designation}
                onChange={handleInputChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-600 outline-none"
              >
                <option value="">Select</option>
                {designations.map((des) => (
                  <option key={des.id} value={des.id}>{des.designation}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Department</label>
              <select
                name="department"
                value={formData.department}
                onChange={handleInputChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-600 outline-none"
              >
                <option value="">Select</option>
                {departments.map((dept) => (
                  <option key={dept.id} value={dept.id}>{dept.department}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                First Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-600 outline-none"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Last Name</label>
              <input
                type="text"
                name="surname"
                value={formData.surname}
                onChange={handleInputChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-600 outline-none"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Father Name</label>
              <input
                type="text"
                name="father_name"
                value={formData.father_name}
                onChange={handleInputChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-600 outline-none"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Mother Name</label>
              <input
                type="text"
                name="mother_name"
                value={formData.mother_name}
                onChange={handleInputChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-600 outline-none"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Email (Login Username) <span className="text-red-500">*</span>
              </label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-600 outline-none"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Gender <span className="text-red-500">*</span>
              </label>
              <select
                name="gender"
                value={formData.gender}
                onChange={handleInputChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-600 outline-none"
                required
              >
                <option value="">Select</option>
                <option value="Male">Male</option>
                <option value="Female">Female</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Date of Birth <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                name="dob"
                value={formData.dob}
                onChange={handleInputChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-600 outline-none"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Date of Joining</label>
              <input
                type="date"
                name="date_of_joining"
                value={formData.date_of_joining}
                onChange={handleInputChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-600 outline-none"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Contact No</label>
              <input
                type="text"
                name="contactno"
                value={formData.contactno}
                onChange={handleInputChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-600 outline-none"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Emergency Contact</label>
              <input
                type="text"
                name="emergency_no"
                value={formData.emergency_no}
                onChange={handleInputChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-600 outline-none"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Marital Status</label>
              <select
                name="marital_status"
                value={formData.marital_status}
                onChange={handleInputChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-600 outline-none"
              >
                <option value="">Select</option>
                <option value="Single">Single</option>
                <option value="Married">Married</option>
                <option value="Divorced">Divorced</option>
                <option value="Widowed">Widowed</option>
              </select>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Local Address</label>
              <textarea
                name="address"
                value={formData.address}
                onChange={handleInputChange}
                rows="3"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-600 outline-none"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Permanent Address</label>
              <textarea
                name="permanent_address"
                value={formData.permanent_address}
                onChange={handleInputChange}
                rows="3"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-600 outline-none"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Qualification</label>
              <input
                type="text"
                name="qualification"
                value={formData.qualification}
                onChange={handleInputChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-600 outline-none"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Work Experience</label>
              <input
                type="text"
                name="work_exp"
                value={formData.work_exp}
                onChange={handleInputChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-600 outline-none"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Note</label>
              <input
                type="text"
                name="note"
                value={formData.note}
                onChange={handleInputChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-600 outline-none"
              />
            </div>
          </div>
        </div>

        {/* Payroll Information */}
        <div className="mb-8">
          <h2 className="text-xl font-bold text-gray-900 mb-4 pb-2 border-b">Payroll Information</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">EPF No</label>
              <input
                type="text"
                name="epf_no"
                value={formData.epf_no}
                onChange={handleInputChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-600 outline-none"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Basic Salary</label>
              <input
                type="number"
                name="basic_salary"
                value={formData.basic_salary}
                onChange={handleInputChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-600 outline-none"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Contract Type</label>
              <select
                name="contract_type"
                value={formData.contract_type}
                onChange={handleInputChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-600 outline-none"
              >
                <option value="">Select</option>
                <option value="Permanent">Permanent</option>
                <option value="Probation">Probation</option>
                <option value="Contract">Contract</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Shift</label>
              <input
                type="text"
                name="shift"
                value={formData.shift}
                onChange={handleInputChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-600 outline-none"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Location</label>
              <input
                type="text"
                name="location"
                value={formData.location}
                onChange={handleInputChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-600 outline-none"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Commission %</label>
              <input
                type="number"
                name="commision"
                value={formData.commision}
                onChange={handleInputChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-600 outline-none"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Discount %</label>
              <input
                type="number"
                name="discount"
                value={formData.discount}
                onChange={handleInputChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-600 outline-none"
              />
            </div>
          </div>
        </div>

        {/* Leave Details */}
        <div className="mb-8">
          <h2 className="text-xl font-bold text-gray-900 mb-4 pb-2 border-b">Leave Details</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Festival Leave</label>
              <input
                type="number"
                name="festival_leave"
                value={formData.festival_leave}
                onChange={handleInputChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-600 outline-none"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Emergency Leave</label>
              <input
                type="number"
                name="emergency_leave"
                value={formData.emergency_leave}
                onChange={handleInputChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-600 outline-none"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Regular Leave</label>
              <input
                type="number"
                name="regular_leave"
                value={formData.regular_leave}
                onChange={handleInputChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-600 outline-none"
              />
            </div>
          </div>
        </div>

        {/* Bank Account Details */}
        <div className="mb-8">
          <h2 className="text-xl font-bold text-gray-900 mb-4 pb-2 border-b">Bank Account Details</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Account Title</label>
              <input
                type="text"
                name="account_title"
                value={formData.account_title}
                onChange={handleInputChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-600 outline-none"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Bank Account No</label>
              <input
                type="text"
                name="bank_account_no"
                value={formData.bank_account_no}
                onChange={handleInputChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-600 outline-none"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Bank Name</label>
              <input
                type="text"
                name="bank_name"
                value={formData.bank_name}
                onChange={handleInputChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-600 outline-none"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">IFSC Code</label>
              <input
                type="text"
                name="ifsc_code"
                value={formData.ifsc_code}
                onChange={handleInputChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-600 outline-none"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Bank Branch</label>
              <input
                type="text"
                name="bank_branch"
                value={formData.bank_branch}
                onChange={handleInputChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-600 outline-none"
              />
            </div>
          </div>
        </div>

        {/* Social Media Links */}
        <div className="mb-8">
          <h2 className="text-xl font-bold text-gray-900 mb-4 pb-2 border-b">Social Media Links</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Facebook</label>
              <input
                type="url"
                name="facebook"
                value={formData.facebook}
                onChange={handleInputChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-600 outline-none"
                placeholder="https://facebook.com/username"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Twitter</label>
              <input
                type="url"
                name="twitter"
                value={formData.twitter}
                onChange={handleInputChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-600 outline-none"
                placeholder="https://twitter.com/username"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">LinkedIn</label>
              <input
                type="url"
                name="linkedin"
                value={formData.linkedin}
                onChange={handleInputChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-600 outline-none"
                placeholder="https://linkedin.com/in/username"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Instagram</label>
              <input
                type="url"
                name="instagram"
                value={formData.instagram}
                onChange={handleInputChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-600 outline-none"
                placeholder="https://instagram.com/username"
              />
            </div>
          </div>
        </div>

        {/* Upload Documents */}
        <div className="mb-8">
          <h2 className="text-xl font-bold text-gray-900 mb-4 pb-2 border-b">Upload Documents</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Photo</label>
              {photoPreview && (
                <img src={photoPreview} alt="Preview" className="w-24 h-24 object-cover rounded-lg mb-2 border-2 border-gray-200" />
              )}
              <label className="flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg cursor-pointer border border-gray-300 w-fit">
                <FiUpload />
                Choose Photo
                <input type="file" name="photo" onChange={handleFileChange} accept="image/*" className="hidden" />
              </label>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Resume</label>
              <label className="flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg cursor-pointer border border-gray-300 w-fit">
                <FiUpload />
                Choose Resume
                <input type="file" name="resume" onChange={handleFileChange} accept=".pdf,.doc,.docx" className="hidden" />
              </label>
              {formData.resume && <p className="text-sm text-gray-600 mt-1">{formData.resume.name}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Other Document</label>
              <label className="flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg cursor-pointer border border-gray-300 w-fit">
                <FiUpload />
                Choose Document
                <input type="file" name="other_doc" onChange={handleFileChange} className="hidden" />
              </label>
              {formData.other_doc && <p className="text-sm text-gray-600 mt-1">{formData.other_doc.name}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Joining Letter</label>
              <label className="flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg cursor-pointer border border-gray-300 w-fit">
                <FiUpload />
                Choose Letter
                <input type="file" name="joining_letter" onChange={handleFileChange} accept=".pdf,.doc,.docx" className="hidden" />
              </label>
              {formData.joining_letter && <p className="text-sm text-gray-600 mt-1">{formData.joining_letter.name}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Resignation Letter</label>
              <label className="flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg cursor-pointer border border-gray-300 w-fit">
                <FiUpload />
                Choose Letter
                <input type="file" name="resignation_letter" onChange={handleFileChange} accept=".pdf,.doc,.docx" className="hidden" />
              </label>
              {formData.resignation_letter && <p className="text-sm text-gray-600 mt-1">{formData.resignation_letter.name}</p>}
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-3 pt-6 border-t">
          <button
            type="button"
            onClick={() => navigate('/teachers')}
            className="px-6 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors"
            disabled={submitting}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="px-6 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg transition-colors"
            disabled={submitting}
          >
            {submitting ? (id ? 'Updating...' : 'Adding...') : (id ? 'Update Staff' : 'Add Staff')}
          </button>
        </div>
      </form>
    </div>
  );
};

export default AddTeacher;

